/**
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║                            CounselCore™ Auth Context                          ║
 * ║              Authentication and Authorization Context Provider                ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 */

import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';
import { clearCache } from '../utils/apiCache';
import { storeRefreshToken, clearRefreshToken } from '../utils/tokenRefresh';

// API Base URL
const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// Create Auth Context
export const AuthContext = createContext(null);

// Custom hook to use Auth Context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          // Migration-aware read: prefer sessionStorage, fall back to legacy localStorage
          const isImpersonating =
            sessionStorage.getItem('is_impersonating') === 'true' ||
            localStorage.getItem('is_impersonating') === 'true';
          
          // ONE round-trip auth bootstrap: returns user + organization
          // together. Replaces the legacy waterfall (/auth/me → wait → 
          // /organizations/<id>) that added ~480 ms before any page data
          // started loading. See PRODUCTION_PERFORMANCE_DIAGNOSTIC.md.
          // The cached sessionStorage org still wins if present (avoids
          // even this one call); we only hit the network on cold start
          // or after an explicit cache clear.
          const cachedOrg = sessionStorage.getItem('organization');
          const isLandingPage = window.location.pathname === '/' || window.location.pathname === '/login';
          let response;
          let bootstrapOrg = null;
          if (cachedOrg && cachedOrg !== 'undefined' && cachedOrg !== 'null') {
            // Org is cached → cheap /auth/me only
            response = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            // Cold start → one round-trip for both
            const bootstrap = await axios.get(`${API}/auth/bootstrap`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            response = { data: bootstrap.data.user };
            bootstrapOrg = bootstrap.data.organization;
          }
          
          setUser(response.data);
          
          const isSuperAdmin = response.data.role === 'super_admin';
          
          if (isSuperAdmin) {
            document.title = 'CounselCore™ - Platform Administration';
          } else if (!isLandingPage) {
            // 🛡️ CRITICAL: Only use cached org if it belongs to the CURRENT user.
            // Prevents cross-tenant identity leak — e.g. cached Mindful Wellness
            // (demo) org bleeding into a Crestview admin session that previously
            // visited the demo. See iter-99 defense-in-depth.
            const cachedOrgBelongsToUser = (() => {
              if (!cachedOrg || cachedOrg === 'undefined' || cachedOrg === 'null') return false;
              try {
                const parsed = JSON.parse(cachedOrg);
                const cachedId = parsed?.id || parsed?.org_id;
                const userOrgId = response.data.organization_id;
                if (!cachedId || !userOrgId) return false;
                return String(cachedId) === String(userOrgId);
              } catch {
                return false;
              }
            })();
            if (cachedOrgBelongsToUser) {
              try {
                const org = JSON.parse(cachedOrg);
                setOrganization(org);
                if (org.name) {
                  document.title = `${org.name}`;
                }
              } catch (e) {
                console.error('Error parsing stored org:', e);
                sessionStorage.removeItem('organization');
              }
            } else if (cachedOrg) {
              // Stale cache — drop it and refetch from backend for this user
              sessionStorage.removeItem('organization');
            }
            if (!cachedOrgBelongsToUser) {
              if (bootstrapOrg && String(bootstrapOrg?.id || bootstrapOrg?.org_id) === String(response.data.organization_id)) {
                // Org came back inline from /auth/bootstrap — no second call
                setOrganization(bootstrapOrg);
                sessionStorage.setItem('organization', JSON.stringify(bootstrapOrg));
                if (bootstrapOrg.name) {
                  document.title = `${bootstrapOrg.name}`;
                }
              } else if (response.data.organization_id) {
                // Fetch the user's real org from the backend — authoritative
                try {
                  const orgResponse = await axios.get(`${API}/organizations/${response.data.organization_id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setOrganization(orgResponse.data);
                  sessionStorage.setItem('organization', JSON.stringify(orgResponse.data));
                  if (orgResponse.data.name) {
                    document.title = `${orgResponse.data.name}`;
                  }
                } catch (orgError) {
                  console.error('Failed to fetch organization:', orgError);
                }
              }
            }
          }
          
          const now = Date.now();
          localStorage.setItem('lastActivity', now.toString());
        } catch (error) {
          const isImpersonating =
            sessionStorage.getItem('is_impersonating') === 'true' ||
            localStorage.getItem('is_impersonating') === 'true';
          
          if (isImpersonating) {
            console.error('❌ Impersonation token validation failed:', error);
            const originalToken = sessionStorage.getItem('original_token');
            const originalUser = sessionStorage.getItem('original_user') || localStorage.getItem('original_user');
            
            if (originalToken && originalUser) {
              // SECURITY: keep auth tokens in sessionStorage only.
              sessionStorage.setItem('token', originalToken);
              sessionStorage.setItem('authToken', originalToken);
              sessionStorage.setItem('user', originalUser);
              sessionStorage.removeItem('is_impersonating');
              sessionStorage.removeItem('impersonated_user_email');
              sessionStorage.removeItem('original_token');
              sessionStorage.removeItem('original_user');
              // Sweep legacy localStorage keys from pre-migration sessions
              localStorage.removeItem('is_impersonating');
              localStorage.removeItem('impersonated_user_email');
              localStorage.removeItem('authToken');
              localStorage.removeItem('original_user');
              window.location.href = '/platform/admin';
              return;
            }
          }

          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            sessionStorage.removeItem('token');
            clearRefreshToken();
            localStorage.removeItem('lastActivity');
            setToken(null);
            setUser(null);
          } else {
            console.error('⚠️ Auth check failed but keeping session (network error):', error.message);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  useEffect(() => {
    if (!user) {
      document.title = 'CounselCore™ - HIPAA-Compliant Counseling Management Platform';
      localStorage.removeItem('organization');
    }
  }, [user]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      if (response.data.requires_2fa) {
        return { 
          success: false,
          requires_2fa: true,
          temp_token: response.data.temp_token,
          user: response.data.user,
          email: email
        };
      }
      
      const { access_token, refresh_token, user: userData } = response.data;
      
      // Sweep any legacy localStorage tokens from pre-migration sessions
      // (auth tokens now live in sessionStorage only — XSS blast radius mitigation)
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('original_user');
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('impersonated_user_email');
      localStorage.removeItem('original_admin_token');
      localStorage.removeItem('organization');
      
      sessionStorage.setItem('token', access_token);
      sessionStorage.setItem('user', JSON.stringify(userData));
      storeRefreshToken(refresh_token);
      setToken(access_token);
      setUser(userData);
      
      if (userData.organization_id) {
        try {
          const timestamp = Date.now();
          const orgResponse = await axios.get(`${API}/organizations/${userData.organization_id}?_t=${timestamp}`, {
            headers: { 
              Authorization: `Bearer ${access_token}`,
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          });
          console.log('📊 Loaded organization:', orgResponse.data.name);
          setOrganization(orgResponse.data);
          sessionStorage.setItem('organization', JSON.stringify(orgResponse.data));
          
          if (orgResponse.data.name) {
            document.title = orgResponse.data.name;
          }
        } catch (orgError) {
          console.error('Failed to fetch organization:', orgError);
          setOrganization({
            name: 'Your Organization',
            id: userData.organization_id,
            org_id: userData.organization_id
          });
        }
      }
      
      const now = Date.now();
      const sessionExpiry = now + (2 * 60 * 60 * 1000); // 2 hours for healthcare shifts
      localStorage.setItem('lastActivity', now.toString());
      localStorage.setItem('sessionExpiry', sessionExpiry.toString());
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('❌ Login error:', error.response?.data || error.message);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    try {
      let redirectUrl = '/';
      const storedOrg = sessionStorage.getItem('organization');
      let orgData = null;
      
      if (storedOrg && storedOrg !== 'undefined' && storedOrg !== 'null') {
        try {
          orgData = JSON.parse(storedOrg);
        } catch (e) {
          console.error('❌ Failed to parse stored organization:', e);
          sessionStorage.removeItem('organization');
        }
      }
      
      if (!orgData && organization) {
        orgData = organization;
      }
      
      // Determine redirect URL based on org context
      if (orgData) {
        // Prefer fallback_subdomain (shorter, cleaner URL) over slug
        const slug = orgData.fallback_subdomain || orgData.slug;
        if (slug) {
          // Use the subdomain/slug for redirect
          redirectUrl = `/${slug.toLowerCase().replace(/\s+/g, '-')}`;
        }
      } else {
        // Check current path to determine org context
        const currentPath = window.location.pathname.toLowerCase();
        const pathParts = currentPath.split('/').filter(Boolean);
        if (pathParts.length > 0) {
          const firstPart = pathParts[0];
          // Check if first part looks like an org slug (not a system route)
          const systemRoutes = ['login', 'register', 'dashboard', 'platform', 'admin', 'settings', 'profile', 'clients', 'sessions', 'chatbot-analytics', 'client-intakes'];
          if (!systemRoutes.includes(firstPart)) {
            redirectUrl = `/${firstPart}`;
          }
        }
      }
      
      // Clear all auth data
      clearCache();
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('currentOrg');
      sessionStorage.removeItem('original_token');
      sessionStorage.removeItem('original_user');
      sessionStorage.removeItem('is_impersonating');
      sessionStorage.removeItem('impersonated_user_email');
      clearRefreshToken();
      // Keep organization in sessionStorage briefly for login page context
      const orgContext = orgData ? JSON.stringify({ slug: orgData.slug || orgData.fallback_subdomain, name: orgData.name }) : null;
      if (orgContext) {
        sessionStorage.setItem('logout_org_context', orgContext);
      }
      localStorage.removeItem('lastActivity');
      localStorage.removeItem('sessionExpiry');
      localStorage.removeItem('organization');
      // Also sweep any legacy localStorage tokens from pre-migration sessions
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('original_user');
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('impersonated_user_email');
      localStorage.removeItem('original_admin_token');
      setToken(null);
      setUser(null);
      setOrganization(null);
      
      // Redirect to org homepage (not login page)
      console.log(`🚪 Logging out, redirecting to: ${redirectUrl}`);
      window.location.href = redirectUrl;
      
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/';
    }
  };

  const value = useMemo(() => ({
    user,
    token,
    organization,
    login,
    logout,
    loading,
    setUser,
    setToken,
    setOrganization
  // logout closes over `organization`, which is already in deps; safe to omit logout itself.
  }), [user, token, organization, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
